/**
 * Фича «Горячие клавиши редактора».
 *
 *   V — выделение,  N — окно,  E — объект каталога,  C — кабель,  P — труба
 *   Tab — перегородка ⇄ несущая, работает и посреди черчения
 *   R — повернуть выделенный предмет на полу на 90°
 *   Esc — отменить черчение и снять выделение
 *   Delete / Backspace — удалить выделенное; посреди черчения стены — снять последнюю стену
 *   Ctrl/Cmd+Z — отменить (посреди цепочки стен — снять последнюю, цепочка продолжается),
 *   Ctrl/Cmd+Shift+Z — повторить
 *
 * Слушаем на window, но игнорируем ввод в полях — иначе нельзя было бы
 * напечатать «w» в панели свойств.
 */
import { useEffect, useEffectEvent } from "react";
import { findFloorItem } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { floorItemOwner } from "@/shared/store/layers";
import { remove } from "@/shared/store/actions";
import { SelectionKind, Tool, ViewMode, useEditorStore } from "@/widgets/editor";

const TOOL_KEYS: Record<string, Tool> = {
	v: Tool.Select,
	n: Tool.Window,
	e: Tool.Place,
	c: Tool.Cable,
	p: Tool.Pipe,
	// русская раскладка на тех же физических клавишах
	м: Tool.Select,
	т: Tool.Window,
	у: Tool.Place,
	с: Tool.Cable,
	з: Tool.Pipe,
};

interface Params {
	/** Прервать незаконченную цепочку стен */
	onCancelDraft: () => void;
	/** Шаг назад посреди черчения; true — обработан, общая отмена не нужна */
	onUndoDraft?: () => boolean;
	/**
	 * Слушать ли клавиши. Вид, который прячется, а не размонтируется (3D-сцена),
	 * выключает их, пока не на экране, — иначе нажатие сработало бы дважды.
	 */
	enabled?: boolean;
}

export const useEditorHotkeys = ({ onCancelDraft, onUndoDraft, enabled = true }: Params) => {
	// Подписка не пересоздаётся с каждым рендером: Esc при открытой панели сначала закрывает
	// её (`Modal`), стор перерисовывает холст прямо посреди нажатия, и снятый на это время
	// обработчик уже не срабатывал — черновик стены оставался
	const cancelDraft = useEffectEvent(() => onCancelDraft());
	const undoDraft = useEffectEvent(() => onUndoDraft?.() ?? false);
	useEffect(() => {
		if (!enabled) return;
		const handler = (e: KeyboardEvent) => {
			// Состояние — на момент нажатия: снимок из рендера устаревает, а эффект
			// не переподписывается
			const projectStore = useProjectStore.getState();
			const editorStore = useEditorStore.getState();
			const target = e.target as HTMLElement | null;
			if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
			if (editorStore.isReadOnly) return;

			// Tab переключает тип стены, не сбрасывая черновик: следующий сегмент цепочки
			// пойдёт уже другим типом. Вне инструмента стены Tab остаётся браузеру.
			if (
				e.key === "Tab" &&
				editorStore.tool === Tool.Wall &&
				!e.ctrlKey &&
				!e.metaKey &&
				!e.altKey
			) {
				e.preventDefault();
				editorStore.toggleWallRole();
				return;
			}

			if (e.key === "Escape") {
				if (editorStore.viewMode === ViewMode.ThreeD && editorStore.view3d.firstPerson) {
					e.preventDefault();
					editorStore.setView3d({ firstPerson: false });
					return;
				}
				cancelDraft();
				editorStore.setSelection(null);
				editorStore.setTool(Tool.Select);
				return;
			}

			if (e.key === "Delete" || e.key === "Backspace") {
				if (!editorStore.selection) {
					if (undoDraft()) e.preventDefault();
					return;
				}
				e.preventDefault();
				// Удаление отменяется по Ctrl+Z, как любая другая правка
				projectStore.pushHistory();
				remove(editorStore.selection);
				editorStore.setSelection(null);
				return;
			}

			// Поворот предмета на полу: мышью угол не выставить, а 90° — самый частый случай
			if ((e.key === "r" || e.key === "к") && !e.ctrlKey && !e.metaKey && !e.altKey) {
				if (editorStore.selection?.kind !== SelectionKind.Item) return;
				e.preventDefault();
				const item = projectStore.project.layers;
				const found = findFloorItem(item, editorStore.selection.id);
				if (found) {
					projectStore.pushHistory();
					floorItemOwner(found.item.id)
						?.getState()
						.updateFloorItem(found.item.id, { rotation: found.item.rotation + 90 });
				}
				return;
			}

			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
				e.preventDefault();
				if (!e.shiftKey && undoDraft()) return;
				editorStore.setSelection(null);
				if (e.shiftKey) projectStore.redo();
				else projectStore.undo();
				return;
			}

			if (e.ctrlKey || e.metaKey || e.altKey) return;
			const tool = TOOL_KEYS[e.key.toLowerCase()];
			if (tool) {
				cancelDraft();
				editorStore.setTool(tool);
			}
		};

		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [enabled]);
};
